'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// walrus
import Text from '@components/kit/text'
import Box from '@components/kit/box'
import { Column, Row } from '@components/kit/grid'
import FlatList from '@components/kit/flat-list'
import { BoxLink } from '@components/kit/link'
import { PhoneIcon } from '@components/kit/icons'
import AppShell from '@components/ui/app-shell'
import PageHeader from '@components/ui/page-header'
import { mocked_data } from './mocked-data'

/* -----------------------------------------------------------------------------
 * Priority enum and utils
 * -------------------------------------------------------------------------- */

enum Priority {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low'
}

function getPriorityBackgroundColor(priority: string, index: number): string {
  switch (priority) {
    case Priority.High:
      return '#fee2e2' // red-100 - tom leve de vermelho
    case Priority.Medium:
      return '#fef3c7' // amber-100 - tom leve de amarelo
    case Priority.Low:
      return '#dcfce7' // green-100 - tom leve de verde
    default:
      // fallback para o comportamento original (alternando entre branco e azul claro)
      return index % 2 === 0 ? 'white' : 'paleBlue'
  }
}

/* -----------------------------------------------------------------------------
 * RowText & HeaderText
 * -------------------------------------------------------------------------- */

interface RowTextProps {
  textColor?: typeof Text.defaultProps.textColor
  children: React.ReactNode
}

function RowText({ textColor = 'purple', children }: RowTextProps) {
  return (
    <Text fontSize={15} fontWeight={300} textColor={textColor}>
      {children}
    </Text>
  )
}

interface HeaderTextProps {
  children: React.ReactNode
}

function HeaderText({ children }: HeaderTextProps) {
  return (
    <Text fontSize={15} fontWeight='bold' textColor='purple'>
      {children}
    </Text>
  )
}

/* -----------------------------------------------------------------------------
 * QueueHeader
 * -------------------------------------------------------------------------- */

export function QueueHeader() {
  return (
    <Row
      borderTopLeftRadius='medium'
      borderTopRightRadius='medium'
      backgroundColor='paleBlue02'
      borderColor='shade02'
      borderStyle='solid'
      borderRightWidth={1}
      borderBottomWidth={1}
      maxHeight={40}
    >
      <Column width={80} paddingX='20' paddingY='08' justifyContent='center'>
        <HeaderText>Member ID</HeaderText>
      </Column>
      <Column
        width={90}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <HeaderText>Phone Number</HeaderText>
      </Column>
      <Column
        width={160}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <HeaderText>Reason</HeaderText>
      </Column>
      <Column
        width={`calc(40% - 80px - 90px - 160px)`}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <HeaderText>Description</HeaderText>
      </Column>
      <Column
        width={`calc(60% - 80px - 90px - 160px)`}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
        justifyContent='center'
      >
        <HeaderText>Suggested Action</HeaderText>
      </Column>
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * QueueRow
 * -------------------------------------------------------------------------- */

export type QueueRowProps = {
  index: number
  user_id: number
  reason: string
  description: string
  suggested_action: string
  priority: string
}

export function QueueRow({
  index,
  user_id,
  reason,
  description,
  suggested_action,
  priority
}: QueueRowProps) {
  const phoneNumber = '123-456-7890'

  return (
    <Row
      borderColor='shade02'
      style={{ backgroundColor: getPriorityBackgroundColor(priority, index) }}
      borderStyle='solid'
      borderBottomWidth={1}
    >
      <Column width={80} paddingY='08' pl='20'>
        <BoxLink path={`/chat/${user_id}`}>
          <RowText textColor='violet'>{user_id}</RowText>
        </BoxLink>
      </Column>
      <Column
        width={90}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <a
          href={`callto:${phoneNumber}`.replace(/[\s-]/g, '')}
          style={{
            textDecorationLine: 'none',
            marginLeft: '8px',
            alignSelf: 'center'
          }}
        >
          <PhoneIcon width={16} height={16} fill='violet' />
        </a>
      </Column>
      <Column
        width={160}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <RowText>{reason}</RowText>
      </Column>
      <Column
        width={`calc(40% - 80px - 90px - 160px)`}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <RowText>{description}</RowText>
      </Column>
      <Column
        width={`calc(60% - 80px - 90px - 160px)`}
        paddingX='20'
        paddingY='08'
        borderColor='shade02'
        borderStyle='solid'
        borderLeftWidth={1}
      >
        <RowText>{suggested_action}</RowText>
      </Column>
    </Row>
  )
}

/* -----------------------------------------------------------------------------
 * QueueList
 * -------------------------------------------------------------------------- */

type QueueListProps = {
  data: Partial<QueueRowProps>[]
}

export function QueueList({ data }: QueueListProps) {
  return (
    <Box bg='white' borderRadius='medium'>
      <QueueHeader />
      <FlatList
        data={data}
        keyExtractor={(item: QueueRowProps) => item.user_id.toString()}
        renderItem={({ item, index }) => {
          const queueItem = item as QueueRowProps
          return (
            <QueueRow
              index={index}
              user_id={queueItem.user_id}
              reason={queueItem.reason}
              description={queueItem.description}
              suggested_action={queueItem.suggested_action}
              priority={queueItem.priority}
            />
          )
        }}
      />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * Queue
 * -------------------------------------------------------------------------- */

export default function QueuePrioritization() {
  return (
    <AppShell activePage='opportunities' label='QueuePriorization'>
      <PageHeader eyebrow='Queue' title='Priority List' />

      <Box style={{ width: 'fit-content' }}>
        <QueueList data={mocked_data} />
      </Box>
    </AppShell>
  )
}
